import React, { useCallback, useEffect, useState } from 'react';
import { Add } from '@mui/icons-material';
import { Box, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { PermissionsGroupRole, PlatformGroupResponse } from '../../../API';
import { usePlatformGroupsAPI } from '../../../hooks/API/Config/usePlatformGroupsAPI';
import { useLocales } from '../../../hooks';
import { useRecoilValue } from 'recoil';
import { withCurrentUser } from '../../../state/auth';
import Button from '../../shared/Button';
import { PlatformBadge } from '../../shared/Platforms/PlatformBadge';
import { platformGroupsTestIds } from '../../shared/TestsIds';
import PlatformGroupForm from './PlatformGroupForm';

const useStyles = makeStyles()((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    padding: theme.spacing(4)
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(4)
  },
  row: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover
    }
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: theme.spacing(8)
  },
  message: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(3),
    paddingTop: theme.spacing(8),
    color: theme.palette.text.secondary
  }
}));

function PlatformGroupsList(): JSX.Element {
  const { classes } = useStyles();
  const { t } = useLocales();
  const platformGroupsAPI = usePlatformGroupsAPI();
  const currentUser = useRecoilValue(withCurrentUser);
  const canManagePlatformGroups =
    currentUser?.permissionsGroups?.some(
      (permissionsGroup) => permissionsGroup.group === 'ADMIN' && permissionsGroup.role === PermissionsGroupRole.ADMIN
    ) ||
    currentUser?.permissionsGroups?.some(
      (permissionsGroup) =>
        permissionsGroup.group === 'DYNAMIC_MODULES' &&
        (permissionsGroup.role === PermissionsGroupRole.ADMIN || permissionsGroup.role === PermissionsGroupRole.EDITOR)
    );

  const [platformGroups, setPlatformGroups] = useState<PlatformGroupResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<PlatformGroupResponse | undefined>(undefined);

  const fetchPlatformGroups = useCallback(async () => {
    setLoading(true);
    setHasError(false);
    try {
      const { data } = await platformGroupsAPI.getAll();
      setPlatformGroups(data.body);
    } catch {
      setHasError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlatformGroups();
  }, [fetchPlatformGroups]);

  const openCreate = () => {
    setEditingGroup(undefined);
    setFormOpen(true);
  };

  const openEdit = (platformGroup: PlatformGroupResponse) => {
    setEditingGroup(platformGroup);
    setFormOpen(true);
  };

  const onFormClose = (saved: boolean) => {
    setFormOpen(false);
    if (saved) {
      fetchPlatformGroups();
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const showEmptyState = !loading && !hasError && platformGroups.length === 0;
  const showTable = !loading && !hasError && platformGroups.length > 0;

  return (
    <div className={classes.root} data-testid={platformGroupsTestIds.root}>
      <div className={classes.header}>
        <Typography variant="h6">{t('platform_groups.title')}</Typography>
        <Button
          endIcon={<Add />}
          onClick={openCreate}
          disabled={!canManagePlatformGroups}
          data-testid={platformGroupsTestIds.addButton}
        >
          {t('platform_groups.add_group')}
        </Button>
      </div>

      {loading && (
        <div className={classes.loading} data-testid={platformGroupsTestIds.loading}>
          <CircularProgress />
        </div>
      )}

      {hasError && (
        <div className={classes.message} data-testid={platformGroupsTestIds.error}>
          <Typography variant="body1">{t('platform_groups.error_load_failed')}</Typography>
          <Button color="grey" onClick={fetchPlatformGroups} data-testid={platformGroupsTestIds.retryButton}>
            {t('platform_groups.retry')}
          </Button>
        </div>
      )}

      {showEmptyState && (
        <div className={classes.message} data-testid={platformGroupsTestIds.emptyState}>
          <Typography variant="body1">{t('platform_groups.empty_state')}</Typography>
        </div>
      )}

      {showTable && (
        <Box sx={{ overflow: 'auto' }}>
          <Table data-testid={platformGroupsTestIds.table}>
            <TableHead>
              <TableRow>
                <TableCell>{t('platform_groups.column_name')}</TableCell>
                <TableCell>{t('platform_groups.column_platforms')}</TableCell>
                <TableCell>{t('platform_groups.column_updated')}</TableCell>
                <TableCell>{t('platform_groups.column_updated_by')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {platformGroups.map((platformGroup) => (
                <TableRow
                  key={platformGroup.id}
                  className={classes.row}
                  data-testid={platformGroupsTestIds.row}
                  onClick={() => openEdit(platformGroup)}
                >
                  <TableCell>{platformGroup.name}</TableCell>
                  <TableCell>
                    <PlatformBadge platforms={platformGroup.platforms} />
                  </TableCell>
                  <TableCell>{formatDate(platformGroup.updatedDate)}</TableCell>
                  <TableCell>{platformGroup.updatedBy ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      <PlatformGroupForm
        open={formOpen}
        platformGroup={editingGroup}
        canManage={!!canManagePlatformGroups}
        onClose={onFormClose}
      />
    </div>
  );
}

export default PlatformGroupsList;
